import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import { Upload, Trash2, FileText, Save } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCertificatesPage = () => {
  const navigate = useNavigate();
  const [certificates, setCertificates] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [newName, setNewName] = useState('');
  const [newFile, setNewFile] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [editingCert, setEditingCert] = useState(null);
  const [editProducts, setEditProducts] = useState([]);

  const token = localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [certRes, prodRes] = await Promise.all([
        axios.get(`${API}/admin/certificates`, { headers }),
        axios.get(`${API}/products`)
      ]);
      setCertificates(certRes.data);
      setProducts(prodRes.data);
    } catch (err) {
      if (err.response?.status === 401) navigate('/beheer');
    } finally { setLoading(false); }
  }, [headers, navigate]);

  useEffect(() => { fetchData(); }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!newFile || !newName) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('name', newName);
      formData.append('file', newFile);
      formData.append('product_ids', selectedProducts.join(','));
      await axios.post(`${API}/admin/certificates`, formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setNewName(''); setNewFile(null); setSelectedProducts([]);
      fetchData();
    } catch (err) { console.error(err); }
    finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this certificate?')) return;
    await axios.delete(`${API}/admin/certificates/${id}`, { headers });
    fetchData();
  };

  const handleUpdateProducts = async (certId) => {
    const formData = new FormData();
    formData.append('product_ids', editProducts.join(','));
    await axios.put(`${API}/admin/certificates/${certId}`, formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
    setEditingCert(null);
    fetchData();
  };

  const toggleProduct = (list, setList, pid) => {
    setList(prev => prev.includes(pid) ? prev.filter(id => id !== pid) : [...prev, pid]);
  };

  if (loading) return <AdminLayout><div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#BE1622] border-t-transparent rounded-full animate-spin" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto">
        <h1 className="text-2xl font-bold text-slate-900 mb-6">Certificaten Beheer</h1>

        {/* Upload form */}
        <div className="bg-white border border-gray-200 p-6 mb-8">
          <h2 className="text-sm font-bold text-slate-900 mb-4">Nieuw Certificaat Uploaden</h2>
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Certificaat naam" required
                className="w-full px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:border-[#BE1622]" />
              <div className="flex items-center gap-2">
                <label className="flex-1 flex items-center gap-2 px-3 py-2 border border-gray-200 cursor-pointer hover:border-[#BE1622] transition-colors text-sm text-slate-500">
                  <Upload className="w-4 h-4" />
                  {newFile ? newFile.name : 'Kies PDF bestand...'}
                  <input type="file" accept=".pdf" className="hidden" onChange={(e) => setNewFile(e.target.files[0])} />
                </label>
              </div>
            </div>

            {/* Product selection */}
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-2">Producten koppelen (optioneel):</p>
              <div className="max-h-40 overflow-y-auto border border-gray-200 p-2 space-y-1">
                {products.map(p => (
                  <label key={p.id} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer hover:bg-slate-50 px-2 py-1">
                    <input type="checkbox" checked={selectedProducts.includes(p.id)} onChange={() => toggleProduct(selectedProducts, setSelectedProducts, p.id)} className="accent-[#BE1622]" />
                    {p.name_nl}
                  </label>
                ))}
              </div>
            </div>

            <button type="submit" disabled={uploading || !newFile || !newName}
              className="bg-[#BE1622] hover:bg-[#9E111B] text-white font-semibold tracking-wider uppercase text-[11px] px-6 py-2.5 transition-all disabled:opacity-50 flex items-center gap-2">
              <Upload className="w-3.5 h-3.5" /> {uploading ? 'Uploaden...' : 'Uploaden'}
            </button>
          </form>
        </div>

        {/* Certificate list */}
        <div className="space-y-3">
          {certificates.length === 0 ? (
            <p className="text-slate-400 text-sm text-center py-8">Geen certificaten gevonden.</p>
          ) : certificates.map(cert => (
            <div key={cert.id} className="bg-white border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-[#BE1622]" />
                  <div>
                    <h3 className="text-sm font-bold text-slate-900">{cert.name}</h3>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${cert.file_url}`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#BE1622] hover:underline">
                      Download PDF
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => { setEditingCert(editingCert === cert.id ? null : cert.id); setEditProducts(cert.product_ids || []); }}
                    className="text-xs text-slate-500 hover:text-[#BE1622] px-2 py-1 border border-gray-200 hover:border-[#BE1622]/30">
                    {editingCert === cert.id ? 'Sluiten' : 'Producten bewerken'}
                  </button>
                  <button onClick={() => handleDelete(cert.id)} className="text-slate-400 hover:text-red-600 p-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>

              <p className="text-[10px] text-slate-400">{(cert.product_ids || []).length} producten gekoppeld</p>

              {editingCert === cert.id && (
                <div className="mt-3 border-t border-gray-100 pt-3">
                  <div className="max-h-48 overflow-y-auto border border-gray-200 p-2 space-y-1 mb-3">
                    {products.map(p => (
                      <label key={p.id} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer hover:bg-slate-50 px-2 py-1">
                        <input type="checkbox" checked={editProducts.includes(p.id)} onChange={() => toggleProduct(editProducts, setEditProducts, p.id)} className="accent-[#BE1622]" />
                        {p.name_nl}
                      </label>
                    ))}
                  </div>
                  <button onClick={() => handleUpdateProducts(cert.id)}
                    className="bg-[#BE1622] hover:bg-[#9E111B] text-white font-semibold tracking-wider uppercase text-[11px] px-4 py-2 transition-all flex items-center gap-2">
                    <Save className="w-3.5 h-3.5" /> Opslaan
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminCertificatesPage;
